import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useClearOverride,
  useOverrideChecklistItem,
} from '../hooks/use-go-checklist';
import type {
  GoChecklistItem,
  GoChecklistItemStatus,
} from '../types/go-checklist.types';

interface Props {
  item: GoChecklistItem | null;
  onClose: () => void;
}

const STATUS_LABELS: Record<GoChecklistItemStatus, string> = {
  CONFORM: 'Conforme',
  WARNING: 'À surveiller',
  NON_CONFORM: 'Non conforme',
  UNDETERMINABLE: 'Indéterminable',
};

export function ItemOverrideDialog({ item, onClose }: Props) {
  const override = useOverrideChecklistItem();
  const clear = useClearOverride();
  const [overrideStatus, setOverrideStatus] = useState<GoChecklistItemStatus>('CONFORM');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (item) {
      setOverrideStatus(item.manualOverrideStatus ?? item.status);
      setReason(item.manualOverrideReason ?? '');
    }
  }, [item]);

  if (!item) return null;

  const minReached = reason.trim().length >= 50;
  const currentStatus = item.manualOverrideStatus ?? item.status;

  async function handleSubmit() {
    if (!item) return;
    if (!minReached) return;
    await override.mutateAsync({
      itemId: item.id,
      input: { overrideStatus, reason: reason.trim() },
    });
    onClose();
  }

  async function handleClear() {
    if (!item) return;
    await clear.mutateAsync(item.id);
    onClose();
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Override manuel du statut</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md bg-muted p-3">
            <p className="text-xs text-muted-foreground">Exigence</p>
            <p className="text-sm font-medium">{item.requirementText}</p>
            {item.evidenceDescription && (
              <>
                <p className="text-xs text-muted-foreground mt-2">Évidence</p>
                <p className="text-sm">{item.evidenceDescription}</p>
              </>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Statut actuel : <span className="font-medium">{STATUS_LABELS[currentStatus]}</span>
              {item.manualOverride && (
                <span className="ml-2 italic">(overridé manuellement)</span>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="override-status">Nouveau statut</Label>
            <Select
              value={overrideStatus}
              onValueChange={(v) => setOverrideStatus(v as GoChecklistItemStatus)}
            >
              <SelectTrigger id="override-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['CONFORM', 'WARNING', 'NON_CONFORM', 'UNDETERMINABLE'] as const).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="override-reason">
              Justification ({reason.trim().length}/50 min)
            </Label>
            <Textarea
              id="override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Pourquoi forcer ce statut ? (min 50 caractères, tracé dans l'audit)"
            />
            {!minReached && reason.length > 0 && (
              <p className="text-xs text-amber-600">
                Encore {50 - reason.trim().length} caractères requis
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          {item.manualOverride && (
            <Button variant="outline" onClick={handleClear} disabled={clear.isPending}>
              Réinitialiser l'override
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!minReached || override.isPending}>
            Appliquer l'override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
